import { useEffect, useRef, useState } from "react";
import { PiCaretDownBold, PiCheckBold, PiCircleNotchBold, PiDownloadSimpleBold, PiFolderOpenBold, PiScanBold, PiXBold } from "react-icons/pi";
import toast from "react-hot-toast";
import API from "../../api";
import { capture } from "../../instrument";
import { DWT_INSTALLER_URL, EXTRACTION_STEPS, isScannerServiceMissing, loadScannerList, scanToJpeg, SCANNER_STORAGE_KEY } from "../../utils/dynamsoftWebTwain";

const CreateAdmissionModal = ({ laboratories = [], defaultLaboratoryId = "", onClose, onCreated }) => {
  const initialLaboratoryId = defaultLaboratoryId || (laboratories.length === 1 ? laboratories[0]._id : "");
  const [laboratoryId, setLaboratoryId] = useState(initialLaboratoryId);
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractionStepIndex, setExtractionStepIndex] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [formMode, setFormMode] = useState(false);
  const [scanners, setScanners] = useState([]);
  const [scannerName, setScannerName] = useState(() => localStorage.getItem(SCANNER_STORAGE_KEY) || "");
  const [serviceNotInstalled, setServiceNotInstalled] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadScannerList().then(({ scanners: nextScanners, scannerName: nextScannerName, serviceNotInstalled: notInstalled }) => {
      if (cancelled) return;
      setScanners(nextScanners);
      setScannerName((current) => (nextScanners.includes(current) ? current : nextScannerName));
      setServiceNotInstalled(notInstalled);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const onScannerChange = (event) => {
    const name = event.target.value;
    setScannerName(name);
    localStorage.setItem(SCANNER_STORAGE_KEY, name);
  };

  useEffect(() => {
    if (!extracting) {
      setExtractionStepIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setExtractionStepIndex((index) => Math.min(index + 1, EXTRACTION_STEPS.length - 1));
    }, 1200);
    return () => clearInterval(interval);
  }, [extracting]);

  const createAdmission = async (files = []) => {
    if (!laboratoryId) return toast.error("Veuillez sélectionner un laboratoire");

    const isExtraction = files.length > 0;
    setLoading(true);
    if (isExtraction) setExtracting(true);
    try {
      const formData = new FormData();
      formData.append("laboratoryId", laboratoryId);
      formData.append("status", "TO_VALIDATE");
      formData.append("formMode", formMode);
      files.forEach((file) => formData.append("files", file));

      const response = await API.postFormData("/admin/admission", formData);

      if (!response.ok) {
        if (response.code === "NO_VALID_CONTENT_FOUND") toast.error("Aucun contenu valide trouvé dans l'ordonnance");
        else throw new Error(response.message || "Erreur lors de la création de l'admission");
        setLoading(false);
        setExtracting(false);
        return;
      }

      toast.success(isExtraction ? "Admission créée et analysée avec succès" : "Admission créée avec succès");
      onCreated(response.data._id);
    } catch (error) {
      capture(error);
      toast.error(error.message || "Une erreur est survenue lors de la création de l'admission");
      setLoading(false);
      setExtracting(false);
    }
  };

  const onFileChange = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (files.length === 0) return;
    createAdmission(files);
  };

  const onScanClick = async () => {
    if (!laboratoryId) return toast.error("Veuillez sélectionner un laboratoire");

    setScanning(true);
    try {
      const file = await scanToJpeg(scannerName);
      if (!file) {
        toast.error("Aucune page numérisée");
        return;
      }
      await createAdmission([file]);
    } catch (error) {
      if (isScannerServiceMissing(error)) {
        setServiceNotInstalled(true);
        toast.error("Service de numérisation non installé");
      } else {
        capture(error);
        toast.error(error.message || "Erreur lors du scan");
      }
    } finally {
      setScanning(false);
    }
  };

  const busy = loading || scanning;

  return (
    <div className="fixed inset-0 z-[220] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5">
        <div className="flex items-center justify-between gap-3 px-5 pb-4 pt-4">
          <h2 className="text-base font-semibold text-gray-900">Créer une admission</h2>
          {!extracting && (
            <button
              type="button"
              className="-m-1 rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
              onClick={onClose}
              disabled={busy}
              aria-label="Fermer"
            >
              <PiXBold className="size-4" />
            </button>
          )}
        </div>

        {extracting ? (
          <div className="flex flex-col gap-4 px-5 pb-6 pt-2">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Extraction en cours</h3>
              <p className="mt-0.5 text-xs text-gray-500">Nous analysons votre document.</p>
            </div>
            <ul className="space-y-2.5">
              {EXTRACTION_STEPS.map((step, index) => {
                const isDone = index < extractionStepIndex;
                const isCurrent = index === extractionStepIndex;
                return (
                  <li key={step} className="flex items-center gap-3">
                    <span
                      className={`flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] ${
                        isDone ? "bg-primary-500 text-white" : isCurrent ? "bg-primary-100 text-primary-600" : "bg-gray-100 text-gray-300"
                      }`}
                    >
                      {isDone ? (
                        <PiCheckBold className="size-3" />
                      ) : isCurrent ? (
                        <PiCircleNotchBold className="size-3 animate-spin" />
                      ) : (
                        <span className="size-1 rounded-full bg-current" />
                      )}
                    </span>
                    <span className={`text-sm ${isDone ? "text-gray-400 line-through decoration-gray-300" : isCurrent ? "font-medium text-gray-900" : "text-gray-400"}`}>
                      {step.replace(/…$/, "")}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-3 px-5 pb-5">
              <button
                type="button"
                role="checkbox"
                aria-checked={formMode}
                onClick={() => setFormMode((value) => !value)}
                disabled={busy}
                className="flex items-center gap-2.5 text-left disabled:opacity-50"
              >
                <span
                  className={`flex size-[18px] shrink-0 items-center justify-center rounded-md border transition-colors ${
                    formMode ? "border-primary-500 bg-primary-500 text-white" : "border-gray-300 bg-white text-transparent hover:border-primary-400"
                  }`}
                >
                  <PiCheckBold className="size-2.5" />
                </span>
                <span className="text-sm font-medium text-gray-700">Ordonnance avec case à cocher</span>
              </button>

              {laboratories.length > 1 && (
                <div className="relative">
                  <select
                    className="w-full cursor-pointer appearance-none truncate rounded-md border border-gray-300 bg-white py-2 pl-3 pr-9 text-sm font-medium text-gray-900 outline-none transition-all focus:border-primary-500 focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                    value={laboratoryId}
                    onChange={(e) => setLaboratoryId(e.target.value)}
                    disabled={busy}
                    aria-label="Changer de laboratoire"
                  >
                    <option value="">Sélectionner un laboratoire</option>
                    {laboratories.map((lab) => (
                      <option key={lab._id} value={lab._id}>
                        {lab.name}
                      </option>
                    ))}
                  </select>
                  <PiCaretDownBold className="pointer-events-none absolute right-3 top-1/2 size-3.5 -translate-y-1/2 text-gray-400" />
                </div>
              )}

              <button
                type="button"
                className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-50 px-4 py-2 text-sm font-medium text-primary-600 ring-1 ring-primary-100 transition-colors hover:bg-primary-100 disabled:opacity-50"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy || !laboratoryId}
              >
                <PiFolderOpenBold className="size-4" />
                <span>Depuis un fichier</span>
              </button>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/jpg,application/pdf" multiple className="hidden" onChange={onFileChange} />

              {serviceNotInstalled ? (
                <a
                  href={DWT_INSTALLER_URL}
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
                >
                  <PiDownloadSimpleBold className="size-4" />
                  <span>Installer le driver du scanner</span>
                </a>
              ) : (
                <button
                  type="button"
                  className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:opacity-50"
                  onClick={onScanClick}
                  disabled={busy || !laboratoryId}
                >
                  <PiScanBold className="size-4" />
                  <span>{scanning ? "Scan en cours…" : "Depuis le scanner"}</span>
                </button>
              )}

              {scanners.length > 1 && (
                <div className="-mt-1 flex items-center justify-end px-1 text-xs text-gray-500">
                  <div className="relative min-w-0 flex-1">
                    <select
                      className="w-full cursor-pointer appearance-none truncate bg-transparent pr-5 text-right text-xs font-medium text-gray-700 underline-offset-2 hover:text-primary-600 hover:underline focus:outline-none disabled:opacity-50"
                      value={scannerName}
                      onChange={onScannerChange}
                      disabled={busy}
                      aria-label="Changer de scanner"
                    >
                      {scanners.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center">
                      <PiCaretDownBold className="size-3 text-gray-400" />
                    </div>
                  </div>
                </div>
              )}

              <button
                type="button"
                className="mt-1 self-center text-xs text-gray-500 underline-offset-4 transition-colors hover:text-gray-800 hover:underline disabled:opacity-50"
                onClick={() => createAdmission([])}
                disabled={busy || !laboratoryId}
              >
                ou saisir manuellement
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CreateAdmissionModal;
